<template>
    <div class="episode-menu-wrapper">
        <button class="icon-btn" type="button" aria-label="Episodes" :aria-expanded="visible" @click="$emit('toggle')">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"> <path d="M13.8476 13.317L9.50515 18.2798C8.70833 19.1905 7.29167 19.1905 6.49485 18.2798L2.15238 13.317C1.49259 12.563 1.49259 11.437 2.15238 10.683L6.49485 5.72018C7.29167 4.80952 8.70833 4.80952 9.50515 5.72017L13.8476 10.683C14.5074 11.437 14.5074 12.563 13.8476 13.317Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/> <path d="M13 19L17.8844 13.3016C18.5263 12.5526 18.5263 11.4474 17.8844 10.6984L13 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/> <path d="M17 19L21.8844 13.3016C22.5263 12.5526 22.5263 11.4474 21.8844 10.6984L17 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/> </svg>
        </button>

        <transition name="stream-menu">
            <div class="episode-menu" v-if="visible" @click.stop>
                <div class="episode-menu-arrow"></div>

                <div class="episode-menu-panel" v-if="menuView === 'seasons'">
                    <div class="episode-menu-header">
                        <span class="episode-menu-title">Seasons</span>
                    </div>
                    <div class="episode-menu-list">
                        <button
                            v-for="s in seasons"
                            :key="s"
                            type="button"
                            class="episode-menu-item season-item"
                            :class="{ 'is-active': s === menuSeason }"
                            @click="$emit('change-season', s)"
                        >
                            <span class="episode-menu-item-name">Season {{ s }}</span>
                            <span class="episode-menu-item-dot" v-if="s === currentSeason"></span>
                        </button>
                    </div>
                </div>

                <div class="episode-menu-panel" v-else>
                    <div class="episode-menu-header">
                        <button class="episode-menu-back" type="button" aria-label="Back to seasons" @click="$emit('update:menuView', 'seasons')">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"> <line x1="19" y1="12" x2="5" y2="12" /> <polyline points="12 19 5 12 12 5" /> </svg>
                        </button>
                        <span class="episode-menu-title">Season {{ menuSeason }}</span>
                    </div>
                    <div class="episode-menu-list">
                        <div
                            v-for="(ep, index) in menuEpisodes"
                            :key="index"
                            class="episode-menu-item"
                            :class="{
                                'is-current': menuSeason === currentSeason && (index + 1) === currentEpisode,
                                'is-upcoming': isUpcoming(ep),
                                'is-completed': menuSeason < currentSeason || (menuSeason === currentSeason && (index + 1) < currentEpisode)
                            }"
                            @click="$emit('change-episode', menuSeason, index + 1)"
                        >
                            <template v-if="menuSeason === currentSeason && (index + 1) === currentEpisode && !isUpcoming(ep)">
                                <div class="episode-info-top">
                                        <span class="episode-menu-item-number">{{ index + 1 }}</span>
                                        <span class="episode-menu-item-name">{{ ep.name }}</span>
                                    </div>
                                <div class="episode-thumb">
                                    <img v-if="ep.still_path" :src="'https://image.tmdb.org/t/p/w500/' + ep.still_path" @error="onImgError" />
                                    <div class="episode-thumb-overlay">
                                        <svg viewBox="0 0 24 24" fill="currentColor"> <rect x="4" y="10" width="3" height="6" rx="1"> <animate attributeName="height" values="6;14;6" dur="0.9s" repeatCount="indefinite" /> <animate attributeName="y" values="10;6;10" dur="0.9s" repeatCount="indefinite" /> </rect> <rect x="10.5" y="4" width="3" height="16" rx="1"> <animate attributeName="height" values="16;8;16" dur="0.9s" repeatCount="indefinite" begin="0.15s" /> <animate attributeName="y" values="4;8;4" dur="0.9s" repeatCount="indefinite" begin="0.15s" /> </rect> <rect x="17" y="8" width="3" height="10" rx="1"> <animate attributeName="height" values="10;16;10" dur="0.9s" repeatCount="indefinite" begin="0.3s" /> <animate attributeName="y" values="8;4;8" dur="0.9s" repeatCount="indefinite" begin="0.3s" /> </rect> </svg>
                                        Now Playing
                                    </div>
                                </div>
                                <div class="episode-info">
                                    <div class="episode-menu-item-overview">{{ ep.overview || 'N/A' }}</div>
                                </div>
                            </template>

                            <template v-else>
                                <span class="episode-menu-item-number">{{ index + 1 }}</span>
                                <span class="episode-menu-item-name">{{ ep.name }}</span>
                                <span class="episode-menu-item-soon" v-if="isUpcoming(ep)">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"> <circle cx="12" cy="12" r="9" /> <polyline points="12 7 12 12 15 14" /> </svg>
                                    Coming soon · {{ formatAirDate(ep.air_date) }}
                                </span>
                                <svg class="episode-menu-item-check" v-if="menuSeason < currentSeason || (menuSeason === currentSeason && (index + 1) < currentEpisode)" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"> <polyline points="20 6 9 17 4 12" /> </svg>
                            </template>
                        </div>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
export default {
    name: 'EpisodeMenu',
    props: {
        visible: { type: Boolean, default: false },
        seasons: { type: Number, default: 0 },
        currentSeason: { type: Number, default: 1 },
        currentEpisode: { type: Number, default: 1 },
        menuSeason: { type: Number, default: 1 },
        menuEpisodes: { type: Array, default: () => [] },
        menuView: { type: String, default: 'episodes' },
    },
    methods: {
        // "YYYY-MM-DD" -> local Date (avoids the UTC off-by-one from new Date("2026-10-09"))
        parseAirDate(str) {
            if (!str) return null
            const [y, m, d] = String(str).split('-').map(Number)
            if (!y || !m || !d) return null
            return new Date(y, m - 1, d)
        },
        // null / undefined / invalid air_date => treated as already aired; airing today counts as out
        isUpcoming(ep) {
            const airDate = this.parseAirDate(ep && ep.air_date)
            if (!airDate) return false
            const now = new Date()
            return airDate > new Date(now.getFullYear(), now.getMonth(), now.getDate())
        },
        formatAirDate(str) {
            const d = this.parseAirDate(str)
            return d ? d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : ''
        },
        onImgError(event) {
            event.target.style.display = 'none'
        },
    },
}
</script>

<style lang="scss" scoped>
.episode-menu-wrapper {
    position: relative;
    display: inline-flex;
}

.episode-menu {
    position: absolute;
    bottom: calc(100% + 32px);
    right: -8px;
    z-index: 20;
    width: 400px;
    max-width: 86vw;
    background: rgba(24, 24, 26, 0.94);
    backdrop-filter: blur(18px) saturate(1.4);
    -webkit-backdrop-filter: blur(18px) saturate(1.4);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 10px;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.5), 0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.06);
    overflow: hidden;
    transform-origin: bottom right;

    .episode-menu-arrow {
        position: absolute;
        bottom: -5px;
        right: 28px;
        transform: rotate(45deg);
        width: 10px;
        height: 10px;
        background: rgba(24, 24, 26, 0.94);
        border-right: 1px solid rgba(255, 255, 255, 0.08);
        border-bottom: 1px solid rgba(255, 255, 255, 0.08);
        border-radius: 0 0 2px 0;
    }

    .episode-menu-panel {
        display: flex;
        flex-direction: column;
        max-height: 420px;
    }

    .episode-menu-header {
        display: flex;
        align-items: center;
        gap: 14px;
        padding: 16px 18px;
        border-bottom: 1px solid rgba(255, 255, 255, 0.9);
        flex-shrink: 0;
    }

    .episode-menu-back {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        background: none;
        border: none;
        padding: 0;
        color: #fff;
        cursor: pointer;
        opacity: 0.9;
        transition: opacity 0.15s ease;

        svg {
            width: 22px;
            height: 22px;
        }

        &:hover {
            opacity: 1;
        }
    }

    .episode-menu-title {
        font-size: 19px;
        font-weight: 700;
        color: #fff;
    }

    .episode-menu-list {
        overflow-y: auto;
        display: flex;
        flex-direction: column;

        &::-webkit-scrollbar {
            width: 6px;
        }

        &::-webkit-scrollbar-track {
            background: transparent;
        }

        &::-webkit-scrollbar-thumb {
            background: rgba(255, 255, 255, 0.15);
            border-radius: 3px;

            &:hover {
                background: rgba(255, 255, 255, 0.28);
            }
        }
    }

    .episode-menu-item {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 12px;
        width: calc(100% - 36px);
        background: none;
        border: none;
        border-bottom: 1px solid rgba(255, 255, 255, 0.08);
        padding: 14px 18px;
        color: rgba(255, 255, 255, 0.85);
        font-size: 14px;
        font-weight: 600;
        text-align: left;
        cursor: pointer;
        transition: background 0.15s ease, color 0.15s ease;

        &:last-child {
            border-bottom: none;
        }

        &:hover {
            background: rgba(255, 255, 255, 0.06);
            color: #fff;
        }

        &.season-item {
            width: 100%;
            justify-content: space-between;
            font-size: 15px;

            &.is-active {
                color: #fff;
            }
        }

        &.is-current {
            align-items: flex-start;
            flex-wrap: nowrap;
            background: rgba(255, 255, 255, 0.05);
            border: 1px solid rgba(255, 255, 255, 0.9);
            width: calc(100% - 22px);
            padding: 10px;
            flex-wrap: wrap;
            .episode-info-top{
                width: 100%
            }

        }

        &.is-completed {
            color: rgba(255, 255, 255, 0.5);
        }

        &.is-upcoming {
            .episode-menu-item-number,
            .episode-menu-item-name {
                color: rgba(255, 255, 255, 0.55);
            }
        }
    }

    .episode-menu-item-number {
        flex-shrink: 0;
        min-width: 16px;
        font-size: 16px;
        font-weight: 700;
        color: #fff;
    }

    .episode-menu-item-name {
        flex: 1;
        min-width: 0;
        font-size: 15px;
        font-weight: 700;
        color: #fff;
    }

    .episode-menu-item-check {
        flex-shrink: 0;
        width: 18px;
        height: 18px;
        color: #2ecc71;
    }

    .episode-menu-item-overview {
        flex-basis: 100%;
        font-size: 13px;
        font-weight: 400;
        line-height: 1.5;
        color: rgba(255, 255, 255, 0.65);
    }

    .episode-menu-item-soon {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        flex-basis: 100%;
        padding-left: 28px; // lines up under the episode name (number min-width 16px + gap 12px)
        font-size: 12px;
        font-weight: 600;
        color: #f5b942;

        svg {
            flex-shrink: 0;
            width: 14px;
            height: 14px;
        }
    }

    .episode-menu-item-dot {
        flex-shrink: 0;
        width: 7px;
        height: 7px;
        border-radius: 50%;
        background: #2ecc71;
        box-shadow: 0 0 0 3px rgba(39, 174, 96, 0.2);
    }

    .episode-thumb {
        position: relative;
        flex-shrink: 0;
        width: 160px;
        aspect-ratio: 16 / 9;
        border-radius: 4px;
        overflow: hidden;
        background: rgba(255, 255, 255, 0.08);

        img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            display: block;
        }
    }

    .episode-thumb-overlay {
        position: absolute;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        background: rgba(0, 0, 0, 0.45);
        color: #fff;
        font-size: 12px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.3px;

        svg {
            width: 16px;
            height: 16px;
        }
    }

    .episode-info {
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 6px;
        padding-top: 2px;
    }

    .episode-info-top {
        display: flex;
        align-items: baseline;
        gap: 10px;
    }
}
</style>
